import { loadAudioCatalog, peekAudioCatalog, pickAudioTake, poolEntries, seededRandom, type AudioPools, type AudioTake, type AudioTiers } from '../shared/audio-catalog';
import { decodeAudioTake, hasDecodedAudioTake, peekDecodedAudioTakeInfo } from '../shared/audio-take';
import { embeddedCampaignAudioSuppressed, ftCampaignAudioSurface } from '../shared/audio-surface';
import { footstepPoolKeys, pageEncounter, rollSoundRules } from '../shared/roll-sound-route';
import { footstepSurfaceTiers, type FootstepSurfaceClass } from '../shared/footstep-surface';
import type { FootstepGear } from '../shared/footstep-gear';
import { footstepFeetTiers, footstepSizeVoice, type FootstepFeet, type FootstepWings, type SizeTier } from '../shared/footstep-creature';
import { FOOTSTEP_WALK_MAX_FT_PER_SECOND, type FootstepGait } from './ft3d-foot-contact';
export { stepOnsetSeconds } from '../shared/audio-take';

const takeUrl = (source: AudioPools, entry: string) => source.base + (entry.includes('.') ? entry : `${entry}.mp3`);

/**
 * Seeded first-tier pick, excluding the mover's previous take when alternatives exist. With `warm`, the pick
 * stays among takes already decoded, repeating a lone warm take or trying the next tier when necessary: a stride
 * is shorter than a fetch + decode, so chasing a fresh take per strike leaves every strike cold.
 */
export function pickFootstepTake(source: AudioPools, tiers: AudioTiers, placementId: string, step: number, previousUrl?: string,
  warm?: (url: string) => boolean, seedKind = 'take'): AudioTake | null {
  const random = seededRandom(`${placementId}|${step}|${seedKind}`);
  for (const tier of tiers) {
    const available = Object.fromEntries(tier.map(pool => [pool, poolEntries(source, pool)
      .filter(entry => !warm || warm(takeUrl(source, entry)))]));
    const entries = new Set(Object.values(available).flat());
    if (!entries.size) continue;
    const usable = (entry: string) => entries.size <= 1 || takeUrl(source, entry) !== previousUrl;
    const pools = Object.fromEntries(tier.map(pool => [pool, available[pool]!.filter(usable)]));
    return pickAudioTake({ base: source.base, pools }, [tier], random);
  }
  return null;
}

const warmOrders = new WeakMap<AudioPools, Map<string, string[]>>();
/**
 * Start decoding the first `count` takes of a seeded, memoized order of the first tier with entries. A larger
 * count extends the same order, so the warm set only grows; already-cached takes are free.
 */
export function warmFootstepTakes(source: AudioPools, tiers: AudioTiers, count: number): void {
  let orders = warmOrders.get(source);
  if (!orders) { orders = new Map(); warmOrders.set(source, orders); }
  for (const tier of tiers) {
    const key = tier.join(',');
    let order = orders.get(key);
    if (!order) {
      order = [...new Set(tier.flatMap(pool => poolEntries(source, pool)))].map(entry => takeUrl(source, entry));
      const random = seededRandom(`${key}|warm`);
      for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [order[i], order[j]] = [order[j]!, order[i]!]; }
      orders.set(key, order);
    }
    if (!order.length) continue;
    for (let i = 0; i < Math.min(count, order.length); i++) if (!hasDecodedAudioTake(order[i]!)) void decodeAudioTake(order[i]!).catch(() => {});
    return;
  }
}
const WARM_TAKES_PER_POOL = 4, WARM_TAKES_MAX = 8;

type PositionFt = { x: number; y: number; z: number };
export interface FootstepMove {
  placementId: string; speedVariable: string; progress: number; totalFt: number; durationMs: number;
  pathFt: PositionFt[]; cumulativeFt: number[];
  guide: { climbSegments?: Array<[number, number]> };
  windowClimb?: unknown;
}

/** Ground path length through this frame, excluding climb ranges even across corners or frame hitches. */
export function footstepGroundDistance(move: FootstepMove, previousProgress: number): number {
  return footstepPathDistance(move, previousProgress, false);
}

/** Climb travel includes vertical edges, and overlapping guide ranges count only once. */
export function footstepClimbDistance(move: FootstepMove, previousProgress: number): number {
  return footstepPathDistance(move, previousProgress, true);
}

function footstepPathDistance(move: FootstepMove, previousProgress: number, climbing: boolean): number {
  const allClimb = !!move.windowClimb || move.speedVariable === 'speed_climbing';
  if (move.totalFt <= 0 || (allClimb && !climbing)) return 0;
  const from = previousProgress * move.totalFt, to = move.progress * move.totalFt;
  let distance = 0;
  for (let i = 1; i < move.pathFt.length; i++) {
    const start = move.cumulativeFt[i - 1] ?? 0, end = move.cumulativeFt[i] ?? start;
    const a = Math.max(from, start), b = Math.min(to, end);
    if (b <= a || end <= start) continue;
    // Advance through the union of climb ranges without allocating interval arrays.
    let cursor = a, ground = 0;
    const climbs = move.guide.climbSegments;
    while (!allClimb && cursor < b) {
      let blockedEnd = cursor, nextClimb = b;
      if (climbs) for (let j = 0; j < climbs.length; j++) {
        const range = climbs[j]!, low = range[0] * move.totalFt, high = range[1] * move.totalFt;
        if (high <= low) continue;
        if (low <= cursor && high > blockedEnd) blockedEnd = Math.min(b, high);
        else if (low > cursor && low < nextClimb) nextClimb = low;
      }
      if (blockedEnd > cursor) cursor = blockedEnd;
      else { ground += nextClimb - cursor; cursor = nextClimb; }
    }
    const p = move.pathFt[i - 1]!, q = move.pathFt[i]!;
    const ratio = Math.hypot(q.x - p.x, q.y - p.y, climbing ? q.z - p.z : 0) / (end - start);
    distance += (climbing ? b - a - ground : ground) * ratio;
  }
  return distance;
}

export interface FootstepMover {
  speedType: string; hidden: boolean; surface: FootstepSurfaceClass | null; gear: FootstepGear | null;
  feet: FootstepFeet; size: SizeTier; wings: FootstepWings;
}

/** A hidden mover sneaks: chain and plate still give it away; light armor goes quiet (owner 2026-10-19). */
export function audibleFootstepGear(gear: FootstepGear | null, hidden: boolean): FootstepGear | null {
  if (!gear || !hidden) return gear;
  return gear.armor === 'chain' || gear.armor === 'plate' ? gear : null;
}

type FootstepStroke = 'swim' | 'fly' | 'climb';
const SWIM_TIERS: AudioTiers = [['speed_swimming/']]; // The live subcategory is empty.
const FEATHERED_TIERS: AudioTiers = [['speed_flying/angel_wings'], ['speed_flying/dragon_wings']];
const LEATHERY_TIERS: AudioTiers = [['speed_flying/dragon_wings'], ['speed_flying/angel_wings']];
const METAL_CLIMB_TIERS: AudioTiers = [['speed_climbing/metal'], ['speed_climbing/wood']];
const WOOD_CLIMB_TIERS: AudioTiers = [['speed_climbing/wood'], ['speed_climbing/metal']];
// Medium beats every .45 s; larger wings beat more slowly, independent of distance or gait.
const WINGBEAT_SECONDS: Readonly<Record<SizeTier, number>> = {
  tiny: .25, small: .35, medium: .45, large: .6, huge: .8, gargantuan: 1,
};
function strokeTiers(stroke: FootstepStroke, mover: FootstepMover, catalog?: AudioPools | null): AudioTiers {
  if (stroke === 'swim') return SWIM_TIERS;
  const tiers = stroke === 'fly' ? mover.wings === 'leathery' ? LEATHERY_TIERS : FEATHERED_TIERS
    : mover.surface === 'metal' ? METAL_CLIMB_TIERS : WOOD_CLIMB_TIERS;
  // The other material is a fallback for an EMPTY pool only. A merely-cold preferred pool must not borrow the other
  // pool's decoded takes (a griffon's first beat flapped dragon wings; review 2026-10-14) — it stays cold this stroke.
  return catalog && tiers[0]!.some(pool => poolEntries(catalog, pool).length) ? tiers.slice(0, 1) : tiers;
}
export interface FootstepScenePlayer {
  step(placementId: string, gait: FootstepGait): void;
  move(animation: FootstepMove, speedFtPerSecond: number, rigged: boolean): void;
  dispose(): void;
}
interface FootstepPageAudio {
  context: AudioContext | null;
  players: Set<Ft3dFootstepPlayer>;
  activePlayers: Set<Ft3dFootstepPlayer>;
  reducedData: boolean;
  document: Document;
  /** Why strikes did or did not sound, for smokes and the console (counts only). */
  receipt: Record<string, number>;
  /** The catalog revision and encounter whose walk/run takes were warmed (page-wide, across scene rebuilds). */
  warmed?: { rev: string; encounter: object };
  device(): AudioContext | null;
  unlock(): void;
}
type FootstepAudioOwner = Window & {
  ftCreateSceneFootstepPlayer?: (mover: (id: string) => FootstepMover | null) => FootstepScenePlayer;
  ftUnlockSceneFootstepAudio?: () => void;
  __ftFootstepAudio?: FootstepPageAudio;
};
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'click', 'touchend'] as const;

function topAudioOwner(): FootstepAudioOwner {
  try {
    const top = window.top ?? window.parent ?? window;
    void top.location.href; // Check same-origin access before storing shared state.
    return top as FootstepAudioOwner;
  } catch { return window as FootstepAudioOwner; }
}

/** Page lifetime state survives scene rebuilds and is shared by every bundle copy. */
function pageAudio(): FootstepPageAudio {
  const owner = topAudioOwner();
  if (owner.__ftFootstepAudio) return owner.__ftFootstepAudio;
  const connection = (navigator as Navigator & { connection?: EventTarget & { saveData?: boolean } }).connection;
  const media = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-data: reduce)') : null;
  const state: FootstepPageAudio = {
    context: null, players: new Set(), activePlayers: new Set(), reducedData: false, document: owner.document ?? document, receipt: {},
    device: () => {
      try {
        state.context ??= new AudioContext();
        if (state.context.state !== 'running') { void state.context.resume().catch(() => {}); return null; }
        return state.context;
      } catch { return null; }
    },
    unlock: () => {
      for (const player of state.players) player.refresh();
      if (state.context || (state.activePlayers.size && masterVolume() > 0)) state.device();
    },
  };
  owner.__ftFootstepAudio = state;
  const policyChanged = () => {
    state.reducedData = connection?.saveData === true || media?.matches === true;
    for (const player of state.players) player.refresh();
  };
  policyChanged();
  connection?.addEventListener('change', policyChanged);
  media?.addEventListener('change', policyChanged);
  for (const type of UNLOCK_EVENTS) owner.addEventListener(type, state.unlock, { passive: true });
  owner.ftUnlockSceneFootstepAudio = state.unlock;
  return state;
}

/** Installed by the audio-owning page's mixer; embedded scenes keep their audio suppressed. */
export function installFootstepAudioOwner(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return; // imported by Node tests of the mixer
  if (!embeddedCampaignAudioSuppressed()) {
    pageAudio();
    (window as FootstepAudioOwner).ftCreateSceneFootstepPlayer = mover => new Ft3dFootstepPlayer(mover);
  }
}

export function mountSceneFootsteps(mover: (id: string) => FootstepMover | null): FootstepScenePlayer {
  if (!embeddedCampaignAudioSuppressed() || window.parent === window) return new Ft3dFootstepPlayer(mover);
  let player: FootstepScenePlayer | null = null, disposed = false;
  const audioOwner = topAudioOwner();
  const owner = () => {
    if (!disposed && !player) {
      try { player = audioOwner.ftCreateSceneFootstepPlayer?.(mover) ?? null; }
      catch { /* A cross-origin or still-loading parent has no audio device. */ }
    }
    return disposed ? null : player;
  };
  const dispose = () => {
    disposed = true;
    window.removeEventListener('pagehide', dispose);
    for (const type of UNLOCK_EVENTS) window.removeEventListener(type, unlock);
    player?.dispose(); player = null;
  };
  // User activation belongs to the frame that received the gesture; forward it synchronously.
  const unlock = () => { owner(); audioOwner.ftUnlockSceneFootstepAudio?.(); };
  owner();
  window.addEventListener('pagehide', dispose, { once: true });
  for (const type of UNLOCK_EVENTS) window.addEventListener(type, unlock, { passive: true });
  return { step: (id, gait) => owner()?.step(id, gait),
    move: (animation, speed, rigged) => owner()?.move(animation, speed, rigged), dispose };
}

interface StepMemory {
  counter: number; previousUrl?: string; previousGearUrl?: string; previousWetUrl?: string;
  wetRemaining?: number; wetUntil?: number;
}
const stepMemory = new Map<string, StepMemory>();

function masterVolume(): number {
  const ref = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
  return ref ? ref.muted ? 0 : ref.volume : .8;
}

/** One page device, with disposable scene gains; contacts never wait for downloads or an autoplay unlock. */
export class Ft3dFootstepPlayer {
  private readonly owner = pageAudio();
  private readonly embedded = embeddedCampaignAudioSuppressed();
  private master: GainNode | null = null;
  private control: HTMLAudioElement | null = null;
  private disposed = false;
  private readonly voices = new Set<AudioBufferSourceNode>();
  private readonly travel = new WeakMap<FootstepMove, { progress: number; remainder: number; strokeRemainder: number; wingbeatSeconds: number; departure: boolean }>();
  private readonly prepared = new WeakMap<FootstepMove, string>();
  private readonly controls = () => { if (this.master) this.master.gain.value = masterVolume() * .35; };
  private readonly pagehide = () => this.dispose();

  constructor(private readonly mover: (placementId: string) => FootstepMover | null) {
    this.owner.players.add(this);
    this.refresh();
    // The catalog usually lands after the scene mounts: warm the encounter's takes then, not at the first strike.
    if (!peekAudioCatalog()) void loadAudioCatalog().then(() => this.refresh(), () => {});
    window.addEventListener('pagehide', this.pagehide, { once: true });
  }

  refresh(): boolean {
    const enabled = !this.disposed && typeof AudioContext === 'function' && typeof OfflineAudioContext === 'function'
      && !!peekAudioCatalog() && ftCampaignAudioSurface() && !this.embedded && !this.owner.reducedData;
    const activePlayers = this.owner.activePlayers, element = this.owner.document.documentElement;
    if (enabled) activePlayers.add(this); else activePlayers.delete(this);
    if (activePlayers.size) {
      if (element.dataset.ft3dFootsteps !== '1') element.dataset.ft3dFootsteps = '1';
    }
    else delete element.dataset.ft3dFootsteps;
    if (enabled) this.warmPage();
    return enabled;
  }

  /** Decode the encounter's walk and run takes before anyone moves, so the first move of the page is not silent. */
  private warmPage(): void {
    const catalog = peekAudioCatalog();
    if (!catalog) return;
    const encounter = pageEncounter({}), warmed = this.owner.warmed;
    if (!encounter || (warmed?.rev === catalog.rev && warmed.encounter === encounter) || masterVolume() <= 0) return;
    const rules = rollSoundRules(catalog.rules);
    if (!rules) return;
    this.owner.warmed = { rev: catalog.rev, encounter };
    for (const gait of ['walk', 'run'] as const) {
      warmFootstepTakes(catalog, footstepPoolKeys({ encounter, rules }, 'speed_walking', { gait }), WARM_TAKES_PER_POOL);
    }
  }

  private device(): AudioContext | null {
    try {
      const context = this.owner.device();
      if (!context) return null;
      if (!this.master) { this.master = context.createGain(); this.master.connect(context.destination); }
      const control = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
      if (control !== this.control) {
        this.control?.removeEventListener('volumechange', this.controls);
        this.control = control;
        control?.addEventListener('volumechange', this.controls);
      }
      this.controls();
      return context;
    } catch { return null; }
  }

  private skip(reason: string): void { this.owner.receipt[reason] = (this.owner.receipt[reason] ?? 0) + 1; }

  step(placementId: string, gait: FootstepGait): void {
    this.strike(placementId, gait);
  }

  private strike(placementId: string, gait: FootstepGait, stroke: FootstepStroke | null = null): void {
    if (!stroke) this.skip('strike');
    if (!this.refresh()) return this.skip('inactive');
    const mover = this.mover(placementId);
    if (!mover || (!stroke && mover.speedType !== 'speed_walking')) return this.skip('mover');
    const memory: StepMemory = stepMemory.get(placementId) ?? { counter: 0 };
    stepMemory.set(placementId, memory);
    const step = ++memory.counter;
    const feet = mover.feet ?? 'boot', size = mover.size ?? 'medium', now = Date.now();
    if ((memory.wetUntil ?? 0) <= now) memory.wetRemaining = 0;
    const wetFeet = feet !== 'skitter' && feet !== 'slither';
    const water = stroke === 'swim' || (!stroke && mover.surface === 'water');
    const firstWet = water && !memory.wetRemaining && wetFeet;
    // The countdown is spent only by a strike that actually SOUNDED (below), so a cold first stone take after the river
    // does not swallow the loudest wet layer (review 2026-10-14).
    const wetRemaining = stroke || water ? 0 : memory.wetRemaining ?? 0;
    if (water) { memory.wetRemaining = 6; memory.wetUntil = now + 8000; }
    const catalog = peekAudioCatalog();
    if (!catalog) return this.skip('catalog');
    if (firstWet) {
      warmFootstepTakes(catalog, [['speed_walking/walking_wet']], 2);
      warmFootstepTakes(catalog, [['speed_walking/running_wet']], 2);
    }
    if (masterVolume() <= 0) return this.skip('muted');
    let tiers: AudioTiers;
    if (stroke) { tiers = strokeTiers(stroke, mover, catalog); this.skip(`stroke:${stroke}`); }
    else {
      const rules = rollSoundRules(catalog.rules);
      if (!rules) return this.skip('rules');
      const env = { encounter: pageEncounter({}), rules };
      const fallback = footstepPoolKeys(env, mover.speedType, { gait, hidden: mover.hidden });
      tiers = [...footstepFeetTiers(feet, gait, mover.surface),
        ...(mover.surface ? footstepSurfaceTiers(mover.surface, gait, mover.hidden) : []), ...fallback];
      this.skip(`surface:${mover.surface ?? 'none'}`);
      this.skip(`feet:${feet}`);
    }
    this.skip(`size:${size}`);
    const take = pickFootstepTake(catalog, tiers, placementId, step, memory.previousUrl, hasDecodedAudioTake);
    // Variety grows with use: every fourth strike warms one more take of the same order, up to the cap.
    warmFootstepTakes(catalog, tiers, Math.min(WARM_TAKES_MAX, WARM_TAKES_PER_POOL + (step >> 2)));
    if (!take) return this.skip(tiers.some(tier => tier.some(pool => poolEntries(catalog, pool).length)) ? 'cold' : 'pool');
    const ctx = this.device();
    const decoded = peekDecodedAudioTakeInfo(take.url);
    if (!decoded) { void decodeAudioTake(take.url).catch(() => {}); return this.skip('cold'); }
    if (!ctx) return this.skip(`device-${this.owner.context?.state ?? 'none'}`);
    if (this.voices.size >= 16) return this.skip('voices');
    const { buffer, onsets, nextOnsets } = decoded;
    const index = Math.floor(seededRandom(`${placementId}|${step}|onset`)() * onsets.length);
    const offset = Math.max(0, onsets[index]! - .01);
    // Stop before a neighbouring strike when a take's steps are closer than the usual slice.
    const end = Math.min(buffer.duration, offset + .30, nextOnsets[index]! - .01);
    const duration = end - offset;
    if (!(duration > 0)) return this.skip('slice');
    const voice = footstepSizeVoice(size), level = (mover.hidden ? .5 : 1) * voice.gain;
    if (!this.play(ctx, buffer, offset, duration, level, voice.rate)) return;
    memory.previousUrl = take.url; this.skip('played');
    if (stroke) return; // Swimming remembers water, but strokes never add gear or wet layers.
    if (wetRemaining) memory.wetRemaining = wetRemaining - 1;
    const gear = audibleFootstepGear(mover.gear, mover.hidden);
    if (gear?.armor) memory.previousGearUrl = this.playLayer(ctx, catalog, `armor_step/${gear.armor}`,
      placementId, step, memory.previousGearUrl, level * .8, voice.rate, 'gear', `gear:${gear.armor}`, 'gear-cold', .6);
    if (wetRemaining && wetFeet
      && mover.surface !== 'mud' && mover.surface !== 'snow' && mover.surface !== 'flesh') {
      memory.previousWetUrl = this.playLayer(ctx, catalog, `speed_walking/${gait === 'walk' ? 'walking' : 'running'}_wet`,
        placementId, step, memory.previousWetUrl, level * .6 * (wetRemaining / 6), voice.rate, 'wet', 'wet', 'wet-cold', .5);
    }
  }

  /** Decoded-only whole-take foley. Cold layers start their decode and never play late. */
  private playLayer(ctx: AudioContext, catalog: AudioPools, pool: string, placementId: string, step: number,
    previousUrl: string | undefined, level: number, rate: number, seedKind: string, receipt: string, coldReceipt: string,
    maxDuration: number): string | undefined {
    const tiers = [[pool]];
    const take = pickFootstepTake(catalog, tiers, placementId, step, previousUrl, hasDecodedAudioTake, seedKind);
    if (!take) {
      const cold = pickFootstepTake(catalog, tiers, placementId, step, previousUrl, undefined, seedKind);
      if (cold) { void decodeAudioTake(cold.url).catch(() => {}); this.skip(coldReceipt); }
      return previousUrl;
    }
    const decoded = peekDecodedAudioTakeInfo(take.url);
    if (!decoded) { void decodeAudioTake(take.url).catch(() => {}); this.skip(coldReceipt); return previousUrl; }
    const duration = Math.min(decoded.buffer.duration, maxDuration);
    // Foley keeps headroom under the shared cap so a crowd's tails never drop another mover's FOOTSTEP.
    if (duration > 0 && this.play(ctx, decoded.buffer, 0, duration, level, rate, 12)) {
      this.skip(receipt); return take.url;
    }
    return previousUrl;
  }

  /** Footstep and whole gear takes share the same master, envelope and bounded voice budget. */
  private play(ctx: AudioContext, buffer: AudioBuffer, offset: number, duration: number, level: number, rate = 1, cap = 16): boolean {
    if (this.voices.size >= cap) { this.skip(cap < 16 ? 'gear-voices' : 'voices'); return false; }
    let release = () => {};
    try {
      const source = ctx.createBufferSource(), gain = ctx.createGain(), now = ctx.currentTime;
      release = () => { this.voices.delete(source); source.disconnect(); gain.disconnect(); };
      source.buffer = buffer; source.playbackRate.value = rate; source.connect(gain); gain.connect(this.master!);
      // start(duration) uses buffer seconds; the gain envelope uses elapsed playback seconds.
      const seconds = duration / rate, attack = Math.min(.004, seconds / 2);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(level, now + attack);
      gain.gain.setValueAtTime(level, now + Math.max(attack, seconds - .06));
      gain.gain.linearRampToValueAtTime(0, now + seconds);
      source.onended = release;
      this.voices.add(source);
      source.start(now, offset, duration);
      return true;
    } catch { try { release(); } catch { /* never connected */ } this.skip('start'); return false; }
  }

  move(animation: FootstepMove, speedFtPerSecond: number, rigged: boolean): void {
    let state = this.travel.get(animation);
    if (!state) {
      state = { progress: 0, remainder: 0, strokeRemainder: 0, wingbeatSeconds: .45, departure: true };
      this.travel.set(animation, state);
    }
    const previousProgress = state.progress;
    state.progress = animation.progress;
    const stroke = animation.speedVariable === 'speed_swimming' ? 'swim'
      : animation.speedVariable === 'speed_flying' ? 'fly' : animation.speedVariable === 'speed_climbing' ? 'climb' : null;
    if (!this.refresh() || (!stroke && animation.speedVariable !== 'speed_walking')) {
      // Silent travel is not owed: resuming must not emit a catch-up strike.
      state.remainder = state.strokeRemainder = 0;
      return;
    }
    const catalog = peekAudioCatalog();
    if (catalog && this.prepared.get(animation) !== catalog.rev && masterVolume() > 0) {
      const mover = this.mover(animation.placementId);
      if (!mover) return;
      this.prepared.set(animation, catalog.rev);
      state.wingbeatSeconds = WINGBEAT_SECONDS[mover.size] ?? .45;
      if (stroke || animation.windowClimb || animation.guide.climbSegments?.length) {
        warmFootstepTakes(catalog, strokeTiers(stroke ?? 'climb', mover), WARM_TAKES_PER_POOL);
      }
      const rules = rollSoundRules(catalog.rules);
      if (!stroke && rules && !animation.windowClimb) {
        const env = { encounter: pageEncounter({}), rules };
        // A small body may reach the run band before a human; sprint uses the same pools.
        for (const gait of ['walk', 'run'] as const) {
          if (mover.feet && mover.feet !== 'boot') warmFootstepTakes(catalog, footstepFeetTiers(mover.feet, gait, null), WARM_TAKES_PER_POOL);
          if (mover.surface) warmFootstepTakes(catalog, footstepSurfaceTiers(mover.surface, gait, mover.hidden), WARM_TAKES_PER_POOL);
          warmFootstepTakes(catalog, footstepPoolKeys(env, 'speed_walking', { gait, hidden: mover.hidden }), WARM_TAKES_PER_POOL);
        }
        const gear = audibleFootstepGear(mover.gear, mover.hidden);
        if (gear?.armor) warmFootstepTakes(catalog, [[`armor_step/${gear.armor}`]], WARM_TAKES_PER_POOL);
      }
    }
    if (stroke) {
      // The FIRST stroke lands on departure (review 2026-10-14): waiting a full interval left a huge flyer's 5 ft hop
      // (650 ms < its .8 s beat) completely silent, with the old per-move sound already suppressed. A cold first take
      // keeps the departure pending for the next frame instead of losing it.
      if (state.departure) {
        const cold = this.owner.receipt.cold ?? 0;
        this.strike(animation.placementId, 'walk', stroke);
        if ((this.owner.receipt.cold ?? 0) === cold) { state.departure = false; state.strokeRemainder = 0; }
        return;
      }
      const progress = Math.max(0, animation.progress - previousProgress);
      const interval = stroke === 'fly' ? state.wingbeatSeconds : stroke === 'swim' ? 5 : 2.5;
      state.strokeRemainder += stroke === 'fly' ? progress * (animation.durationMs || 0) / 1000
        : stroke === 'swim' ? progress * animation.totalFt : footstepClimbDistance(animation, previousProgress);
      if (state.strokeRemainder + 1e-9 >= interval) {
        state.strokeRemainder = (state.strokeRemainder + 1e-9) % interval;
        this.strike(animation.placementId, 'walk', stroke);
      }
      return;
    }
    const climbDistance = footstepClimbDistance(animation, previousProgress);
    state.strokeRemainder += climbDistance;
    let climbing = !!animation.windowClimb;
    const climbs = animation.guide.climbSegments;
    if (climbs) for (let i = 0; i < climbs.length; i++) {
      const range = climbs[i]!;
      if (animation.progress >= range[0] && animation.progress <= range[1]) { climbing = true; break; }
    }
    if (climbing) {
      state.remainder = 0;
      if (state.strokeRemainder + 1e-9 >= 2.5) {
        state.strokeRemainder = (state.strokeRemainder + 1e-9) % 2.5;
        this.strike(animation.placementId, 'walk', 'climb');
      }
      return;
    }
    // The frame that LEAVES a climb range still carries that range's last stretch: pay its grip before resetting
    // (review 2026-10-14: a 10 ft wall followed by a roof walk gave 3 grips, not 4).
    if (climbDistance > 0 && state.strokeRemainder + 1e-9 >= 2.5) {
      state.strokeRemainder = 0;
      this.strike(animation.placementId, 'walk', 'climb');
      return;
    }
    state.strokeRemainder = 0;
    if (rigged) return;
    const gait = speedFtPerSecond > FOOTSTEP_WALK_MAX_FT_PER_SECOND ? 'run' : 'walk';
    const stride = gait === 'walk' ? 2.5 : 4;
    state.remainder += footstepGroundDistance(animation, previousProgress);
    if (state.remainder >= stride) {
      state.remainder %= stride; // Drop whole surplus strides after a hitch; never replay a burst.
      this.step(animation.placementId, gait);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.refresh();
    this.owner.players.delete(this);
    window.removeEventListener('pagehide', this.pagehide);
    this.control?.removeEventListener('volumechange', this.controls);
    for (const source of this.voices) { try { source.stop(); } catch { /* already ended */ } }
    this.voices.clear();
    this.master?.disconnect();
  }
}
